import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Nav, Badge, Form, Modal } from 'react-bootstrap';
import Select from 'react-select';
import Breadcrumbs from '../../Common/Breadcrumbs';
import TournamentHeader from '../../Common/TournamentHeader';
import ShimmerLoader from '../../Common/ShimmerLoader';
import SeasonHeroImageUpload from '../../components/SeasonHeroImageUpload/SeasonHeroImageUpload';
import {
  getTournamentById,
  ManageTournament,
  formatDate,
  getTournamentLogoUrl,
  TournamentSeason,
  getAllTournamentSeasons,
  deleteTournamentSeason,
  editTournamentSeason
} from '../../api/tournament/manageTournamentService';
import { hasAnyPermission } from '../../utils/roleBasedAccess';
import { useSelector } from 'react-redux';
// Import tab components - these will need to be updated to accept tournamentSeason id
import AgeCategoryManagement from '../../Common/AgeCategoryManagement';
import TeamsTable from '../TournamentDetails/components/TeamsTable';
import PlayersTable from '../TournamentDetails/components/PlayersTable';
import MatchManagementTable from '../TournamentDetails/components/MatchManagementTable';
import FixturesTable from '../TournamentDetails/components/FixturesTable';
import VotingManagement from '../TournamentDetails/components/VotingManagement';
import PredictionsManagement from '../TournamentDetails/components/PredictionsManagement';
// Import API services for tabs
import { getAllAgeCategoriesPaginated } from '../../api/tournament/ageCategoryService';
import { listTeams } from '../../api/tournament/teamService';
import { listPlayers, addPlayer, editPlayer, deletePlayer } from '../../api/tournament/playerService';
import { listFixtures, downloadFixturesExcelWithFilters, getCalendarEvents, CalendarEvent } from '../../api/tournament/fixturesService';
import MatchCalendar from '../../Common/MatchCalendar';
import { getSportValue as getSportValueUtil } from '../../utils/getSportValue';
import { SEASON_VERTICAL_MEDIA_ENABLED } from '../../constants/featureFlags';
import VideosTab from './Videos/VideosTab';
// Temporarily hidden from the season page.
// import GalleryTab from './Gallery/GalleryTab';
import {
  listTournamentFeatureToggles,
  setTournamentFeatureToggle,
  TournamentFeatureToggle,
} from '../../api/tournament/featureToggleService';
import { getSportAdapter } from '../../api/tournament/sportAdapter';
import {
  isSeasonFeatureEnabled as isExplicitlyEnabled,
  buildTvPassConfig,
  SEASON_LOCATION_ERROR_MESSAGE,
  SEASON_LOCATION_INPUT_PATTERN,
  SEASON_LOCATION_PATTERN,
  setFeatureToggleEnabled,
  validateSeasonDates,
} from './seasonSetup';

type TabType = 'details' | 'age-categories' | 'teams' | 'players' | 'player-stats' | 'match-management' | 'standings' | 'videos' | 'gallery' | 'voting' | 'predictions' | 'settings';

const SeasonDetailPage: React.FC = () => {
  const { tournamentId, seasonId } = useParams<{ tournamentId: string; seasonId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, role, permissions } = useSelector((state: any) => state.Login);

  // Tournament and Season state
  const [tournament, setTournament] = useState<ManageTournament | null>(null);
  const [season, setSeason] = useState<TournamentSeason | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('details');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [featureToggles, setFeatureToggles] = useState<TournamentFeatureToggle[]>([]);
  const [featureToggleError, setFeatureToggleError] = useState<string | null>(null);
  const [featureTogglesLoading, setFeatureTogglesLoading] = useState(false);
  const [savingFeatureToggles, setSavingFeatureToggles] = useState<string[]>([]);

  // Season editing state
  const [isEditingSeason, setIsEditingSeason] = useState(false);
  const [seasonFormData, setSeasonFormData] = useState({
    name: '',
    startDate: '',
    endDate: '',
    location: '',
    seasonYearRange: '',
    status: '',
    isFeaturedOnTv: false,
    showOnHomePage: true,
    tvSortOrder: '2',
    tvPreviewDuration: '',
    sortOrder: '2',
    tvAccessType: 'paid',
    recordingAccessType: 'free',
    highlightAccessType: 'free',
    matchPassEnabled: false,
    matchPassPrice: '',
    seasonPassEnabled: false,
    seasonPassPrice: '',
    tournamentPassEnabled: false,
    heroImage: null as string | null,
    detailHeroImage: null as string | null,
    detailHeroImageApp: null as string | null,
    matchCardBackgroundImage: null as string | null,
  });
  const [seasonMediaTouched, setSeasonMediaTouched] = useState({
    heroImage: false,
    detailHeroImage: false,
    detailHeroImageApp: false,
    matchCardBackgroundImage: false,
  });
  const [seasonMediaUploading, setSeasonMediaUploading] = useState({
    heroImage: false,
    detailHeroImage: false,
    detailHeroImageApp: false,
    matchCardBackgroundImage: false,
  });
  const isSeasonMediaUploading = Object.values(seasonMediaUploading).some(Boolean);
  const [seasonFormErrors, setSeasonFormErrors] = useState<{ [key: string]: string }>({});
  const [seasonFormLoading, setSeasonFormLoading] = useState(false);

  useEffect(() => {
    if (!seasonId) return;
    setFeatureTogglesLoading(true);
    listTournamentFeatureToggles(seasonId)
      .then((toggles) => {
        setFeatureToggles(toggles);
        setFeatureToggleError(null);
      })
      .catch(() => {
        setFeatureToggles([]);
        setFeatureToggleError('Tournament settings are temporarily unavailable.');
      })
      .finally(() => {
        setFeatureTogglesLoading(false);
      });
  }, [seasonId]);

  const updateFeatureToggle = async (toggle: TournamentFeatureToggle, enabled: boolean) => {
    if (!seasonId || savingFeatureToggles.includes(toggle.name)) return;
    const previousEnabled = toggle.enabled;
    setFeatureToggleError(null);
    setSavingFeatureToggles((current) => [...current, toggle.name]);
    setFeatureToggles((current) => setFeatureToggleEnabled(current, toggle.name, enabled));

    try {
      setFeatureToggles(await setTournamentFeatureToggle(seasonId, toggle.name, enabled));
    } catch (err: any) {
      setFeatureToggles((current) => setFeatureToggleEnabled(current, toggle.name, previousEnabled));
      setFeatureToggleError(err?.message || 'Unable to update tournament setting.');
    } finally {
      setSavingFeatureToggles((current) => current.filter((name) => name !== toggle.name));
    }
  };

  const isSeasonFeatureEnabled = (name: string) =>
    isExplicitlyEnabled(featureToggles, name);
  const sportAdapter = getSportAdapter(tournament?.sports || '');
  const supports = (feature: 'fixtures' | 'scoring' | 'playerStats' | 'standings' | 'ties' | 'content') =>
    sportAdapter.features.includes(feature);

  // Tab functionality state
  const [ageCategories, setAgeCategories] = useState([]);
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState<any[]>([]);
  const [fixtures, setFixtures] = useState<any[]>([]);

  // Age category tab state
  const [ageCategorySearch, setAgeCategorySearch] = useState('');
  const [ageCategoriesLoading, setAgeCategoriesLoading] = useState(false);
  const [ageCategoryPage, setAgeCategoryPage] = useState(1);
  const [ageCategoryPageSize, setAgeCategoryPageSize] = useState(10);
  const [ageCategoryTotalPages, setAgeCategoryTotalPages] = useState(1);
  const [ageCategoryTotalCount, setAgeCategoryTotalCount] = useState(0);
  const [ageCategoryRefresh, setAgeCategoryRefresh] = useState(false);

  // Teams tab state
  const [teamSearch, setTeamSearch] = useState('');
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [teamsPage, setTeamsPage] = useState(1);
  const [teamsPageSize, setTeamsPageSize] = useState(10);
  const [teamsTotalPages, setTeamsTotalPages] = useState(1);
  const [teamsTotalCount, setTeamsTotalCount] = useState(0);
  const [teamsAgeCategoryFilter, setTeamsAgeCategoryFilter] = useState<any>(null);
  const [teamsRefresh, setTeamsRefresh] = useState(false);

  // Players tab state
  const [playerSearch, setPlayerSearch] = useState('');
  const [playersLoading, setPlayersLoading] = useState(false);
  const [playersPage, setPlayersPage] = useState(1);
  const [playersPageSize, setPlayersPageSize] = useState(10);
  const [playersTotalPages, setPlayersTotalPages] = useState(1);
  const [playersTotalCount, setPlayersTotalCount] = useState(0);
  const [playersRefresh, setPlayersRefresh] = useState(false);

  // Player Stats tab state
  const [playerStats, setPlayerStats] = useState<any[]>([]);
  const [playerStatsSearch, setPlayerStatsSearch] = useState('');
  const [playerStatsLoading, setPlayerStatsLoading] = useState(false);
  const [playerStatsPage, setPlayerStatsPage] = useState(1);
  const [playerStatsPageSize, setPlayerStatsPageSize] = useState(10);
  const [playerStatsTotalPages, setPlayerStatsTotalPages] = useState(1);
  const [playerStatsTotalCount, setPlayerStatsTotalCount] = useState(0);
  const [playerStatsRefresh, setPlayerStatsRefresh] = useState(false);
  const [playerStatsAgeCategoryFilter, setPlayerStatsAgeCategoryFilter] = useState<any>(null);

  // Fixtures tab state
  const [fixtureSearch, setFixtureSearch] = useState('');
  const [fixturesAgeCategoryFilter, setFixturesAgeCategoryFilter] = useState<{ value: string; label: string } | null>(null);
  const [matchStatusFilter, setMatchStatusFilter] = useState<{ value: string; label: string } | null>(null);
  const [fixturesLoading, setFixturesLoading] = useState(false);
  const [fixturesPage, setFixturesPage] = useState(1);
  const [fixturesPageSize, setFixturesPageSize] = useState(50);
  const [fixturesTotalPages, setFixturesTotalPages] = useState(1);
  const [fixturesTotalCount, setFixturesTotalCount] = useState(0);
  const [fixturesRefresh, setFixturesRefresh] = useState(false);

  // Standings tab state
  const [standings, setStandings] = useState<any[]>([]);
  const [standingsSearch, setStandingsSearch] = useState('');
  const [standingsLoading, setStandingsLoading] = useState(false);
  const [standingsPage, setStandingsPage] = useState(1);
  const [standingsPageSize, setStandingsPageSize] = useState(10);
  const [standingsTotalPages, setStandingsTotalPages] = useState(1);
  const [standingsTotalCount, setStandingsTotalCount] = useState(0);
  const [standingsError, setStandingsError] = useState<string | null>(null);
  const [standingsRefresh, setStandingsRefresh] = useState(false);

  // Filter state for age categories
  const [typeFilter, setTypeFilter] = useState<any>(null);
  const [formatFilter, setFormatFilter] = useState<any>(null);

  // Player age category filter
  const [playersAgeCategoryFilter, setPlayersAgeCategoryFilter] = useState<any>(null);

  // Excel download state
  const [showDownloadExcelModal, setShowDownloadExcelModal] = useState(false);
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
  const [excelDownloadStartDate, setExcelDownloadStartDate] = useState<Date | null>(null);
  const [excelDownloadEndDate, setExcelDownloadEndDate] = useState<Date | null>(null);
  const [excelDownloadStatus, setExcelDownloadStatus] = useState<'pending' | 'completed' | ''>('');
  const [excelDownloadError, setExcelDownloadError] = useState<string | null>(null);
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [loadingCalendarEvents, setLoadingCalendarEvents] = useState(false);

  // Age category options - Always include default options: "Group" and "Single"
  const typeOptions = React.useMemo(() => {
    // Default options that should always be available
    const defaultOptions = [
      { value: 'group', label: 'Group' },
      { value: 'single', label: 'Single' }
    ];

    if (!ageCategories || ageCategories.length === 0) {
      // If no data, return only default options
      return defaultOptions;
    }

    // Extract unique standings types from existing data
    const uniqueTypes = Array.from(new Set(
      ageCategories
        .map((ac: any) => ac.standingsType)
        .filter((type: any) => type && type.trim() !== '')
    )).sort();

    // Map to options format
    const dynamicOptions = uniqueTypes.map((type: string) => ({
      value: type.toLowerCase(),
      label: type.charAt(0).toUpperCase() + type.slice(1)
    }));

    // Merge default options with dynamic options, avoiding duplicates
    const allOptions = [...defaultOptions];
    dynamicOptions.forEach(opt => {
      if (!allOptions.find(o => o.value === opt.value)) {
        allOptions.push(opt);
      }
    });

    return allOptions;
  }, [ageCategories]);

  const formatOptions = React.useMemo(() => {
    return [
      { value: 'team', label: 'Team' },
      { value: 'player', label: 'Player' }
    ];
  }, []);

  // Age category options for players/teams
  const ageCategoryOptionsForPlayers = React.useMemo(() => {
    if (!ageCategories || ageCategories.length === 0) return [];
    return ageCategories.map((ac: any) => ({
      value: ac._id, // Use ID for value
      label: ac.ageCategory || 'Unknown', // Use age category name for label
      format: ac.format, // Include format to check if it's 'player' or 'Team'
      standingsType: ac.standingsType // Include standingsType for fixtures to determine if groups should be loaded
    }));
  }, [ageCategories]);

  // Team options
  const teamOptions = React.useMemo(() => {
    if (!teams || teams.length === 0) return [];
    return teams.map((team: any) => ({
      value: team._id,
      label: team.name || team.shortName || 'Unknown'
    }));
  }, [teams]);

  // Check if user has access
  const userData = {
    ...user,
    subRole: role,
    permissions: permissions,
  };

  const canAccessFixtures = hasAnyPermission(userData, [
    'tournament.admin',
    'tournament.manage',
    'tournament.tournaments.view',
    'tournament.tournaments.manage',
    'tournament.fixtures.manage',
    'tournament.view',
    'tournament.read',
  ]);
  const canManageTournament = hasAnyPermission(userData, [
    'tournament.admin',
    'tournament.manage',
    'tournament.tournaments.manage',
    'tournament.fixtures.manage',
  ]);

  // Load tournament and season details
  useEffect(() => {
    if (tournamentId && seasonId && canAccessFixtures) {
      // Load tournament first, then season (season can use tournament.sports if available)
      loadTournamentDetails().then(() => {
        // After tournament is loaded, load season details
        loadSeasonDetails();
      }).catch(() => {
        // Even if tournament loading fails, try to load season with fallback sport
        loadSeasonDetails();
      });
    }
  }, [tournamentId, seasonId, canAccessFixtures]);

  // Helper function to get sport value safely using shared utility
  const getSport = useCallback((): string => {
    try {
      return getSportValueUtil(tournament, location);
    } catch (error) {
      // Direct links may carry sport in the query, but must never silently
      // dispatch a fixture request as Football.
      const urlParams = new URLSearchParams(location.search);
      const sportFromUrl = urlParams.get('sport');
      if (sportFromUrl) {
        return sportFromUrl;
      }
      throw error;
    }
  }, [tournament, location]);

  // Helper function to check if sport is Badminton or Table Tennis
  const isBadmintonOrTT = useCallback((): boolean => {
    try {
      const sport = getSport().toLowerCase();
      return sport === 'badminton' || sport === 'table tennis' || sport === 'tabletennis' || sport === 'tt';
    } catch {
      return false;
    }
  }, [getSport]);

  // Helper function to check if sport is specifically Badminton
  const isBadminton = useCallback((): boolean => {
    try {
      const sport = getSport().toLowerCase();
      return sport === 'badminton';
    } catch {
      return false;
    }
  }, [getSport]);

  // Note: Standings tab is now available for Badminton/TT to show knockouts

  const loadTournamentDetails = async () => {
    if (!tournamentId) return;

    try {
      const tournamentData = await getTournamentById(tournamentId);
      setTournament(tournamentData);
      // Clear any previous errors if successful
      if (error && error.includes('Failed to load tournament details')) {
        setError(null);
      }
    } catch (error: any) {
      console.error('Error loading tournament details:', error);
      setError('Failed to load tournament details');
    }
  };

  const loadSeasonDetails = async () => {
    if (!tournamentId || !seasonId) return;

    try {
      setLoading(true);
      const seasons = await getAllTournamentSeasons(tournamentId);
      const seasonData = seasons.find(s => s._id === seasonId);

      if (seasonData) {
        setSeason(seasonData);
        // Clear any previous errors if successful
        if (error && (error.includes('Failed to load season details') || error.includes('Season not found'))) {
          setError(null);
        }
      } else {
        setError('Season not found');
      }
    } catch (error: any) {
      console.error('Error loading season details:', error);
      setError('Failed to load season details');
    } finally {
      setLoading(false);
    }
  };

  // Handler functions
  const handleAddAgeCategory = () => {
    // Add age category functionality to be implemented
  };

  const handleEditAgeCategory = () => {
    // Edit age category functionality to be implemented
  };

  const handleDeleteAgeCategory = () => {
    // Delete age category functionality to be implemented
  };

  const handleAddTeam = () => {
    // Refresh teams list after adding
    setTeamsRefresh(v => !v);
  };

  const handleEditTeam = () => {
    // Refresh teams list after editing
    setTeamsRefresh(v => !v);
  };

  const handleDeleteTeam = () => {
    // Refresh teams list after deleting
    setTeamsRefresh(v => !v);
  };

  const handleAddPlayer = async (payload: any) => {
    try {
      await addPlayer(payload, getSport());
      setPlayersRefresh((v: boolean) => !v);
    } catch (error) {
      console.error('Error adding player:', error);
      throw error; // Re-throw to let PlayersTable handle the error display
    }
  };

  const handleEditPlayer = async (payload: any) => {
    try {
      await editPlayer(payload._id, payload);
      setPlayersRefresh((v: boolean) => !v);
    } catch (error) {
      console.error('Error editing player:', error);
      throw error; // Re-throw to let PlayersTable handle the error display
    }
  };

  const handleDeletePlayer = async (playerId: string, sport: string) => {
    try {
      await deletePlayer(playerId, sport);
      setPlayersRefresh((v: boolean) => !v);
    } catch (error) {
      console.error('Error deleting player:', error);
      throw error; // Re-throw to let PlayersTable handle the error display
    }
  };

  const handleAddFixture = () => {
    // Refresh fixtures list after adding
    setFixturesRefresh(v => !v);
  };

  const handleEditFixture = () => {
    // Refresh fixtures list after editing
    setFixturesRefresh(v => !v);
  };

  const handleDeleteFixture = () => {
    // Refresh fixtures list after deleting
    setFixturesRefresh(v => !v);
  };

  // Check if tournament can be deleted (not ongoing or completed)
  const canDeleteTournament = (tournament: ManageTournament | null): boolean => {
    if (!tournament) return false;
    const status = (tournament.status || '').toLowerCase();
    return status !== 'ongoing' && status !== 'completed';
  };

  // Check if season can be deleted (not ongoing or completed, and tournament must also allow deletion)
  const canDeleteSeason = (season: TournamentSeason | null): boolean => {
    if (!season) return false;
    const seasonStatus = (season.status || '').toLowerCase();
    const seasonCanBeDeleted = seasonStatus !== 'ongoing' && seasonStatus !== 'completed';

    // Also check if tournament allows deletion
    const tournamentCanBeDeleted = canDeleteTournament(tournament);

    // Season can only be deleted if both season and tournament allow deletion
    return seasonCanBeDeleted && tournamentCanBeDeleted;
  };

  // Calculate season year range from dates
  const calculateSeasonYearRange = (startDate: string, endDate: string): string => {
    if (!startDate || !endDate) return '';
    const startYear = new Date(startDate).getFullYear();
    const endYear = new Date(endDate).getFullYear();
    return `${startYear}-${endYear}`;
  };

  // Handle edit season button click
  const handleEditSeasonClick = () => {
    if (!season) return;
    setSeasonFormData({
      name: season.seasonLabel || '',
      startDate: season.startDate ? season.startDate.split('T')[0] : '',
      endDate: season.endDate ? season.endDate.split('T')[0] : '',
      location: season.location || '',
      seasonYearRange: season.seasonYearRange || '',
      status: season.status || '',
      isFeaturedOnTv: season.isFeaturedOnTv ?? true,
      showOnHomePage: season.showOnHomePage ?? true,
      tvSortOrder: (season.tvSortOrder ?? 2).toString(),
      tvPreviewDuration: season.tvPreviewDuration?.toString() || '',
      sortOrder: (season.sortOrder ?? 2).toString(),
      tvAccessType: season.tvAccessType ?? 'paid',
      recordingAccessType: season.recordingAccessType ?? 'free',
      highlightAccessType: season.highlightAccessType ?? 'free',
      matchPassEnabled: season.tvPassConfig?.match?.enabled ?? false,
      matchPassPrice: season.tvPassConfig?.match?.pricing?.IN?.amount?.toString() || '',
      seasonPassEnabled: season.tvPassConfig?.season?.enabled ?? false,
      seasonPassPrice: season.tvPassConfig?.season?.pricing?.IN?.amount?.toString() || '',
      tournamentPassEnabled: season.tvPassConfig?.tournament?.enabled ?? false,
      heroImage: season.heroImage || null,
      detailHeroImage: season.detailHeroImage || null,
      detailHeroImageApp: season.detailHeroImageApp || null,
      matchCardBackgroundImage: season.matchCardBackgroundImage || null,
    });
    setSeasonMediaTouched({ heroImage: false, detailHeroImage: false, detailHeroImageApp: false, matchCardBackgroundImage: false });
    setIsEditingSeason(true);
    setSeasonFormErrors({});
    setDeleteError(null);
    setSuccessMessage(null);
  };

  // Handle cancel season edit
  const handleCancelSeasonEdit = () => {
    setIsEditingSeason(false);
    setSeasonFormData({
      name: '',
      startDate: '',
      endDate: '',
      location: '',
      seasonYearRange: '',
      status: '',
      isFeaturedOnTv: false,
      showOnHomePage: true,
      tvSortOrder: '2',
      tvPreviewDuration: '',
      sortOrder: '2',
      tvAccessType: 'paid',
      recordingAccessType: 'free',
      highlightAccessType: 'free',
      matchPassEnabled: false,
      matchPassPrice: '',
      seasonPassEnabled: false,
      seasonPassPrice: '',
      tournamentPassEnabled: false,
      heroImage: null,
      detailHeroImage: null,
      detailHeroImageApp: null,
      matchCardBackgroundImage: null,
    });
    setSeasonMediaTouched({ heroImage: false, detailHeroImage: false, detailHeroImageApp: false, matchCardBackgroundImage: false });
    setSeasonFormErrors({});
    setDeleteError(null);
  };

  const validateSeasonForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!seasonFormData.name || seasonFormData.name.trim().length === 0) {
      errors.name = 'Season name is required';
    }

    if (!seasonFormData.startDate) {
      errors.startDate = 'Start date is required';
    }

    if (!seasonFormData.endDate) {
      errors.endDate = 'End date is required';
    }

    if (!seasonFormData.location || seasonFormData.location.trim().length === 0) {
      errors.location = 'Location is required';
    } else {
      if (!SEASON_LOCATION_PATTERN.test(seasonFormData.location.trim())) {
        errors.location = SEASON_LOCATION_ERROR_MESSAGE;
      }
    }

    if (!seasonFormData.seasonYearRange || seasonFormData.seasonYearRange.trim().length === 0) {
      errors.seasonYearRange = 'Season year range is required';
    } else {
      const yearRangePattern = /^(\d{4})(-\d{4})?$/;
      const trimmedRange = seasonFormData.seasonYearRange.trim();
      if (!yearRangePattern.test(trimmedRange)) {
        errors.seasonYearRange = 'Season year range must be in format "2025" or "2025-2026"';
      } else {
        if (trimmedRange.includes('-')) {
          const [startYear, endYear] = trimmedRange.split('-').map(Number);
          if (endYear < startYear) {
            errors.seasonYearRange = 'End year must be greater than or equal to start year';
          }
        }
      }
    }

    Object.assign(errors, validateSeasonDates(seasonFormData.startDate, seasonFormData.endDate));

    if (!seasonFormData.status || seasonFormData.status.trim().length === 0) {
      errors.status = 'Status is required';
    }

    setSeasonFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSeasonMediaChange = (
    field: 'heroImage' | 'detailHeroImage' | 'detailHeroImageApp' | 'matchCardBackgroundImage',
    value: string | null,
  ) => {
    setSeasonFormData((current) => ({ ...current, [field]: value }));
    setSeasonMediaTouched((current) => ({ ...current, [field]: true }));
  };

  const handleSeasonMediaUploadingChange = (
    field: 'heroImage' | 'detailHeroImage' | 'detailHeroImageApp' | 'matchCardBackgroundImage',
    uploading: boolean,
  ) => {
    setSeasonMediaUploading((current) => ({ ...current, [field]: uploading }));
  };

  const getSeasonMediaContext = (variant: 'portrait' | 'horizontal' | 'horizontal-app' | 'match-card-background') =>
    `tournament-seasons/${season?._id || seasonId || 'new'}/media/${variant}`;

  const buildSeasonMediaPayload = () => {
    const payload: {
      heroImage?: string | null;
      detailHeroImage?: string | null;
      detailHeroImageApp?: string | null;
      matchCardBackgroundImage?: string | null;
    } = {};

    if (SEASON_VERTICAL_MEDIA_ENABLED && seasonMediaTouched.heroImage) {
      payload.heroImage = seasonFormData.heroImage;
    }
    if (seasonMediaTouched.detailHeroImage) {
      payload.detailHeroImage = seasonFormData.detailHeroImage;
    }
    if (seasonMediaTouched.detailHeroImageApp) {
      payload.detailHeroImageApp = seasonFormData.detailHeroImageApp;
    }
    if (seasonMediaTouched.matchCardBackgroundImage) {
      payload.matchCardBackgroundImage = seasonFormData.matchCardBackgroundImage;
    }

    return payload;
  };

  // Handle season form submission
  const handleSeasonFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateSeasonForm() || !season?._id || !tournamentId) {
      return;
    }

    try {
      setSeasonFormLoading(true);
      setDeleteError(null);
      setSuccessMessage(null);

      const startDate = seasonFormData.startDate;
      const endDate = seasonFormData.endDate;
      const seasonYearRange = seasonFormData.seasonYearRange.trim() || calculateSeasonYearRange(startDate, endDate);
      const location = seasonFormData.location.trim();

      // Edit existing season - preserve seasonNumber and seasonType from existing season
      await editTournamentSeason(season._id, getSport(), {
        seasonType: season.seasonType || 'SEASON',
        seasonNumber: season.seasonNumber || 1,
        seasonLabel: seasonFormData.name.trim(),
        seasonYearRange: seasonYearRange,
        location: location,
        startDate: startDate,
        endDate: endDate,
        status: seasonFormData.status,
        isFeaturedOnTv: seasonFormData.isFeaturedOnTv,
        showOnHomePage: seasonFormData.showOnHomePage,
        tvSortOrder: Number(seasonFormData.tvSortOrder),
        tvPreviewDuration: seasonFormData.tvPreviewDuration === '' ? null : Number(seasonFormData.tvPreviewDuration),
        sortOrder: Number(seasonFormData.sortOrder),
        tvAccessType: seasonFormData.tvAccessType as 'free' | 'paid',
        recordingAccessType: seasonFormData.recordingAccessType as 'free' | 'paid',
        highlightAccessType: seasonFormData.highlightAccessType as 'free' | 'paid',
        tvPassConfig: buildTvPassConfig({
          matchEnabled: seasonFormData.tvAccessType === 'paid' && seasonFormData.matchPassEnabled,
          matchPrice: seasonFormData.matchPassPrice,
          seasonEnabled: seasonFormData.seasonPassEnabled,
          seasonPrice: seasonFormData.seasonPassPrice,
          tournamentEnabled: seasonFormData.tournamentPassEnabled,
        }),
        ...buildSeasonMediaPayload(),
      });

      setSuccessMessage('Season updated successfully!');
      setIsEditingSeason(false);

      // Reload season details to get updated data
      await loadSeasonDetails();

      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to update season';
      setDeleteError(errorMessage);
      if (/already exists/i.test(errorMessage)) {
        setSeasonFormErrors((current) => ({
          ...current,
          name: errorMessage,
        }));
      }
    } finally {
      setSeasonFormLoading(false);
    }
  };

  // Handle delete season
  const handleDeleteSeason = async () => {
    if (!season) return;

    // Double check if deletion is allowed
    if (!canDeleteSeason(season)) {
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${season.seasonLabel}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeleteLoading(true);
      setDeleteError(null);
      await deleteTournamentSeason(season._id, getSport());
      setSuccessMessage('Season deleted successfully!');
      // Navigate back to seasons listing page
      setTimeout(() => {
        navigate(`/manage-tournaments-fixtures/${tournamentId}/seasons`, {
          state: {
            selectedTournament: tournament,
            allTournaments: (location.state as any)?.allTournaments
          }
        });
      }, 1000);
    } catch (error: any) {
      setDeleteError(error.message || 'Failed to delete season');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDownloadExcel = async () => {
    // Open modal for filter selection
    setShowDownloadExcelModal(true);
    setExcelDownloadError(null);
    setExcelDownloadStartDate(null);
    setExcelDownloadEndDate(null);
    setExcelDownloadStatus('');

    // Fetch calendar events - pass tournamentSeason
    if (tournamentId) {
      setLoadingCalendarEvents(true);
      try {
        const events = await getCalendarEvents(tournamentId, getSport(), seasonId);
        setCalendarEvents(events);
      } catch (err) {
        console.error('Error fetching calendar events:', err);
        setCalendarEvents([]);
      } finally {
        setLoadingCalendarEvents(false);
      }
    }
  };

  const handleCloseDownloadExcelModal = () => {
    setShowDownloadExcelModal(false);
    setExcelDownloadStartDate(null);
    setExcelDownloadEndDate(null);
    setExcelDownloadStatus('');
    setExcelDownloadError(null);
  };

  const handleExcelDownloadSubmit = async () => {
    if (!tournamentId || !seasonId) {
      setExcelDownloadError('Tournament ID or Season ID not found');
      return;
    }

    // Validation
    if (!excelDownloadStartDate) {
      setExcelDownloadError('Please select a start date');
      return;
    }

    if (!excelDownloadStatus) {
      setExcelDownloadError('Please select a status');
      return;
    }

    // If only start date is selected, use it as both start and end date
    const startDate = excelDownloadStartDate;
    const endDate = excelDownloadEndDate || excelDownloadStartDate;

    // Format dates as YYYY-MM-DD
    const formatDate = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    setIsDownloadingExcel(true);
    setExcelDownloadError(null);

    try {
      // Get the S3 URL from the API - tournamentSeason is required in URL path
      const excelUrl = await downloadFixturesExcelWithFilters(tournamentId, {
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
        status: excelDownloadStatus as 'pending' | 'completed',
        sports: getSport(),
        tournamentSeason: seasonId // Required tournamentSeason ID in URL path
      });

      if (!excelUrl || typeof excelUrl !== 'string') {
        throw new Error('Invalid Excel URL received from server');
      }

      // Generate filename with tournament name, season name, and date range
      const tournamentName = tournament?.name || 'tournament';
      const seasonName = season?.seasonLabel || season?.seasonYearRange || 'season';
      const sanitizedTournamentName = tournamentName.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      const sanitizedSeasonName = seasonName.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      const dateRange = excelDownloadEndDate
        ? `${formatDate(startDate)}-to-${formatDate(endDate)}`
        : formatDate(startDate);
      const filename = `fixtures-${sanitizedTournamentName}-${sanitizedSeasonName}-${dateRange}.xlsx`;

      // Download the file from S3 URL
      try {
        const fileResponse = await fetch(excelUrl, {
          method: 'GET',
          mode: 'cors',
        });

        if (!fileResponse.ok) {
          throw new Error(`Failed to download file: ${fileResponse.status} ${fileResponse.statusText}`);
        }

        // Get the file as a blob
        const blob = await fileResponse.blob();

        if (!blob || blob.size === 0) {
          throw new Error('Downloaded file is empty');
        }

        // Create a blob URL and trigger download
        const blobUrl = URL.createObjectURL(blob);

        // Create and configure download link
        const downloadLink = document.createElement('a');
        downloadLink.href = blobUrl;
        downloadLink.download = filename;
        downloadLink.style.position = 'fixed';
        downloadLink.style.top = '-1000px';
        downloadLink.style.left = '-1000px';

        // Append to body
        document.body.appendChild(downloadLink);

        // Trigger download immediately
        downloadLink.click();

        // Clean up: remove link and revoke blob URL after a short delay
        setTimeout(() => {
          document.body.removeChild(downloadLink);
          URL.revokeObjectURL(blobUrl);
        }, 100);

        // Close modal on success
        handleCloseDownloadExcelModal();

      } catch (fetchError: any) {
        console.error('Error downloading file from S3:', fetchError);
        // Fallback: open URL directly in new tab
        window.open(excelUrl, '_blank');
        handleCloseDownloadExcelModal();
      }

    } catch (error: any) {
      console.error('Error downloading Excel:', error);
      setExcelDownloadError(error.message || 'Failed to download Excel file. Please try again.');
    } finally {
      setIsDownloadingExcel(false);
    }
  };

  const refreshAgeCategories = () => {
    setAgeCategoryPage(1);
    setAgeCategoryRefresh(v => !v); // Toggle refresh state to trigger useEffect
  };

  // Load age categories when Age Category tab is active
  useEffect(() => {
    if (activeTab === 'age-categories' && seasonId) {
      setAgeCategoriesLoading(true);
      // Pass tournamentSeason id instead of tournament id
      getAllAgeCategoriesPaginated(
        tournamentId || '',
        ageCategoryPage,
        ageCategoryPageSize,
        ageCategorySearch,
        (typeFilter as any)?.value || '',
        (formatFilter as any)?.value || '',
        seasonId, // Pass tournamentSeason id
        getSport() // Pass sport parameter - use tournament.sports if loaded, otherwise get from location/URL
      )
        .then(data => {
          const arr = data?.paginatedResults || [];
          setAgeCategories(arr);
          setAgeCategoryTotalPages(data?.totalPages || 1);
          setAgeCategoryTotalCount(data?.totalCount || 0);
          setAgeCategoriesLoading(false);
        })
        .catch(() => {
          setAgeCategoriesLoading(false);
        });
    }
  }, [activeTab, seasonId, tournamentId, ageCategoryPage, ageCategoryPageSize, ageCategorySearch, typeFilter, formatFilter, ageCategoryRefresh]);

  // Load age category options for teams, players, player stats, and fixtures tabs
  useEffect(() => {
    if ((activeTab === 'teams' || activeTab === 'players' || activeTab === 'player-stats' || activeTab === 'match-management') && seasonId && tournamentId) {
      // Use the age category listing API to get age categories for filters and forms
      // No format filter applied here so all age categories for the season are available
      getAllAgeCategoriesPaginated(
        tournamentId || '',
        1,
        100, // Load more for dropdowns
        '',
        '',
        '',  // no format filter - show all age categories
        seasonId, // Pass tournamentSeason id
        getSport() // Pass sport parameter - use tournament.sports if loaded, otherwise get from location/URL
      )
        .then(data => {
          const ageCategoriesData = data?.paginatedResults || [];

          // For teams tab, populate the main ageCategories state with mapped data
          if (activeTab === 'teams') {
            const mapped = ageCategoriesData.map((ac: any) => ({
              ...ac,
              format: ac.format || '-',
              tournament: ac.tournament?.name || ac.tournament || '',
            }));
            setAgeCategories(mapped);
          } else {
            // For players, player stats, and match management tabs, set ageCategories
            // ageCategoryOptionsForPlayers will be computed automatically via useMemo
            setAgeCategories(ageCategoriesData);
          }
        })
        .catch(() => {
          setAgeCategories([]);
        });
    }
  }, [activeTab, seasonId, tournamentId]);

  // Load teams when Teams tab is active
  useEffect(() => {
    if (activeTab === 'teams' && seasonId && tournamentId) {
      setTeamsLoading(true);
      // Pass tournamentSeason id instead of tournament id
      listTeams({
        page: teamsPage,
        pageSize: teamsPageSize,
        tournamentId: tournamentId,
        ageCategory: teamsAgeCategoryFilter?.value || '',
        search: teamSearch,
        gender: '',
        tournamentSeason: seasonId, // Pass tournamentSeason id
        sport: getSport() // Pass sport parameter - use tournament.sports if loaded, otherwise get from location/URL
      })
        .then(data => {
          setTeams(data.paginatedResults || []);
          setTeamsTotalPages(data.totalPages || 1);
          setTeamsTotalCount(data.totalCount || 0);
          setTeamsLoading(false);
        })
        .catch(() => {
          setTeamsLoading(false);
        });
    }
  }, [activeTab, seasonId, tournamentId, teamsPage, teamsPageSize, teamSearch, teamsAgeCategoryFilter, teamsRefresh]);

  // Load team options for Players tab dropdown (separate from Teams tab)
  useEffect(() => {
    if ((activeTab === 'players' || activeTab === 'player-stats') && seasonId && tournamentId) {
      listTeams({
        page: 1,
        pageSize: 100,
        tournamentId: tournamentId,
        tournamentSeason: seasonId, // Pass tournamentSeason id
        sport: getSport() // Pass sport parameter - use tournament.sports if loaded, otherwise get from location/URL
      })
        .then(data => {
          const teamsData = data?.paginatedResults || [];
          // Update teamOptions by updating teams state
          // Note: This is separate from the Teams tab teams state
          // We'll create a separate state for player dropdown teams
          const options = teamsData.map((team: any) => ({ value: team._id, label: team.name }));
          // Update teams state if it's empty or if we're on players tab
          if (teams.length === 0 || activeTab === 'players' || activeTab === 'player-stats') {
            setTeams(teamsData);
          }
        })
        .catch(() => {
          // Don't set empty array, keep existing teams if any
        });
    }
  }, [activeTab, seasonId, tournamentId]);

  // Load players when Players tab is active
  useEffect(() => {
    if (activeTab === 'players' && seasonId && tournamentId) {
      setPlayersLoading(true);
      // Pass tournamentSeason id instead of tournament id
      listPlayers({
        search: playerSearch,
        page: playersPage,
        pageSize: playersPageSize,
        tournament: tournamentId,
        team: '',
        ageCategory: playersAgeCategoryFilter?.label || '', // Use age category filter value
        tournamentSeason: seasonId, // Pass tournamentSeason id
        sport: getSport() // Pass sport parameter - use tournament.sports if loaded, otherwise get from location/URL
      })
        .then(data => {
          // Ensure we always set an array, even if data structure is unexpected
          const rawPlayersList = Array.isArray(data?.paginatedResults)
            ? data.paginatedResults
            : Array.isArray(data)
              ? data
              : [];

          // Transform API response structure to match component expectations
          // API returns: { playerInfo: { name, uniqueId, profilePic }, team: {...}, ageCategory: "..." }
          // Component expects: { name, uniqueId, profilePic, team: {...}, ageCategory: "..." }
          const transformedPlayers = rawPlayersList.map((player: any) => {
            // If playerInfo exists, flatten it to the root level
            if (player.playerInfo) {
              return {
                ...player,
                name: player.playerInfo.name || player.name || '',
                uniqueId: player.playerInfo.uniqueId || player.uniqueId || player._id,
                profilePic: player.playerInfo.profilePic || player.profilePic || null,
                // Keep team structure as-is (already correct)
                // Keep ageCategory as-is (already correct)
                // Keep playerStats as-is (already correct)
              };
            }
            // If no playerInfo, return as-is (already in correct format)
            return {
              ...player,
              name: player.name || '',
              uniqueId: player.uniqueId || player._id,
              profilePic: player.profilePic || null,
            };
          });

          setPlayers(transformedPlayers);
          setPlayersTotalPages(data?.totalPages || 1);
          setPlayersTotalCount(data?.totalCount || 0);
          setPlayersLoading(false);
        })
        .catch((error) => {
          console.error('❌ SeasonDetailPage - Error loading players:', error);
          setPlayersLoading(false);
        });
    }
  }, [activeTab, seasonId, tournamentId, playersPage, playersPageSize, playerSearch, playersAgeCategoryFilter, playersRefresh]);

  // Load players for Player Stats tab when active
  useEffect(() => {
    if (activeTab === 'player-stats' && seasonId && tournamentId) {
      setPlayerStatsLoading(true);
      // Pass tournamentSeason id instead of tournament id
      listPlayers({
        search: playerStatsSearch,
        page: playerStatsPage,
        pageSize: playerStatsPageSize,
        tournament: tournamentId,
        team: '',
        ageCategory: playerStatsAgeCategoryFilter?.label || '', // Use age category filter value
        tournamentSeason: seasonId, // Pass tournamentSeason id
        sport: getSport() // Pass sport parameter - use tournament.sports if loaded, otherwise get from location/URL
      })
        .then(data => {
          // Ensure we always set an array, even if data structure is unexpected
          const rawPlayersList = Array.isArray(data?.paginatedResults)
            ? data.paginatedResults
            : Array.isArray(data)
              ? data
              : [];

          // Transform API response structure to match component expectations
          // API returns: { playerInfo: { name, uniqueId, profilePic }, team: {...}, ageCategory: "..." }
          // Component expects: { name, uniqueId, profilePic, team: {...}, ageCategory: "..." }
          const transformedPlayers = rawPlayersList.map((player: any) => {
            // If playerInfo exists, flatten it to the root level
            if (player.playerInfo) {
              return {
                ...player,
                name: player.playerInfo.name || player.name || '',
                uniqueId: player.playerInfo.uniqueId || player.uniqueId || player._id,
                profilePic: player.playerInfo.profilePic || player.profilePic || null,
                // Keep team structure as-is (already correct)
                // Keep ageCategory as-is (already correct)
                // Keep playerStats as-is (already correct)
              };
            }
            // If no playerInfo, return as-is (already in correct format)
            return {
              ...player,
              name: player.name || '',
              uniqueId: player.uniqueId || player._id,
              profilePic: player.profilePic || null,
            };
          });

          setPlayerStats(transformedPlayers);
          setPlayerStatsTotalPages(data?.totalPages || 1);
          setPlayerStatsTotalCount(data?.totalCount || 0);
          setPlayerStatsLoading(false);
        })
        .catch((error) => {
          console.error('❌ SeasonDetailPage - Error loading players for player-stats:', error);
          setPlayerStatsLoading(false);
        });
    } else if (activeTab === 'player-stats') {
      console.log('⏭️ SeasonDetailPage - Skipping player-stats load:', {
        activeTab,
        seasonId,
        tournamentId
      });
    }
  }, [activeTab, seasonId, tournamentId, playerStatsPage, playerStatsPageSize, playerStatsSearch, playerStatsAgeCategoryFilter, playerStatsRefresh]);

  // Load fixtures when Match Management tab is active
  useEffect(() => {
    if (activeTab === 'match-management' && seasonId && tournamentId) {
      setFixturesLoading(true);
      // Pass tournamentSeason id instead of tournament id
      listFixtures({
        page: fixturesPage,
        pageSize: fixturesPageSize,
        tournamentId: tournamentId,
        search: fixtureSearch,
        ageCategory: fixturesAgeCategoryFilter?.value || '', // Use value (ObjectId/_id), not label (name)
        status: matchStatusFilter?.value || '',
        sport: getSport(),
        tournamentSeason: seasonId // Pass tournamentSeason id
      })
        .then(data => {
          console.log('Fixtures API response:', data);
          setFixtures(data.paginatedResults || []);
          setFixturesTotalPages(data.totalPages || 1);
          setFixturesTotalCount(data.totalCount || 0);
          setFixturesLoading(false);
        })
        .catch((error) => {
          console.error('Error loading fixtures:', error);
          console.error('Fixtures API params:', {
            tournamentId,
            tournamentSeason: seasonId,
            ageCategory: fixturesAgeCategoryFilter?.label || '',
            page: fixturesPage,
            pageSize: fixturesPageSize
          });
          setFixtures([]);
          setFixturesTotalPages(1);
          setFixturesTotalCount(0);
          setFixturesLoading(false);
        });
    }
  }, [activeTab, seasonId, tournamentId, fixturesPage, fixturesPageSize, fixtureSearch, fixturesAgeCategoryFilter, matchStatusFilter, tournament, fixturesRefresh]);

  // Reset standings page when switching to standings tab
  useEffect(() => {
    if (activeTab === 'standings') {
      setStandingsPage(1);
    }
  }, [activeTab]);

  // Load age categories for Standings tab (using age category API with tournamentSeason)
  useEffect(() => {
    if (activeTab === 'standings' && seasonId && tournamentId) {
      setStandingsLoading(true);
      setStandingsError(null);
      try {
        const sport = getSport();
        console.log('Loading age categories for standings tab (SeasonDetailPage):', { tournamentId, seasonId, sport, page: standingsPage });
        getAllAgeCategoriesPaginated(
          tournamentId,
          standingsPage,
          standingsPageSize,
          standingsSearch,
          (typeFilter as any)?.value || '',
          (formatFilter as any)?.value || '',
          seasonId, // Pass tournamentSeason id
          sport // Pass sport parameter
        )
          .then(data => {
            console.log('Age categories loaded for standings (SeasonDetailPage):', data);
            const arr = data?.paginatedResults || [];
            // Map the data similar to age categories tab to ensure consistent structure
            const mapped = arr.map((ac: any) => ({
              ...ac,
              format: ac.format || '-',
              tournament: ac.tournament?.name || ac.tournament || '',
            }));
            setStandings(mapped);
            setStandingsTotalPages(data?.totalPages || 1);
            setStandingsTotalCount(data?.totalCount || 0);
            setStandingsLoading(false);
          })
          .catch((err) => {
            console.error('Error loading age categories for standings (SeasonDetailPage):', err);
            setStandingsError('Failed to load age categories.');
            setStandingsLoading(false);
          });
      } catch (error) {
        console.error('Error getting sport for standings (SeasonDetailPage):', error);
        setStandingsError('Failed to determine sport.');
        setStandingsLoading(false);
      }
    } else if (activeTab !== 'standings') {
      // Clear standings data when switching away from standings tab
      setStandings([]);
    }
  }, [activeTab, seasonId, tournamentId, standingsPage, standingsPageSize, standingsSearch, typeFilter, formatFilter, standingsRefresh, getSport]);

  // Handle activeTab from navigation state
  useEffect(() => {
    if (location.state?.activeTab &&
      typeof location.state.activeTab === 'string' &&
      ['details', 'age-categories', 'teams', 'players', 'player-stats', 'match-management', 'standings', 'videos', 'gallery'].includes(location.state.activeTab)) {
      setActiveTab(location.state.activeTab as TabType);
    }
  }, [location.state]);

  // Redirect away from player-stats tab if sport is badminton
  useEffect(() => {
    if (activeTab === 'player-stats' && isBadminton()) {
      setActiveTab('details');
    }
  }, [activeTab, isBadminton]);

  // Access denied for non-admin users
  if (!canAccessFixtures) {
    return (
      <Container fluid>
        <div className="text-center py-5">
          <h4>Access Denied</h4>
          <p>Only super-admin and operations-admin users can access this module.</p>
        </div>
      </Container>
    );
  }

  const breadcrumbItems = [
    { label: 'Tournament Registrations', onClick: () => navigate('/manage-tournaments-fixtures') },
    {
      label: tournament?.name || 'Tournament', onClick: () => navigate(`/manage-tournaments-fixtures/${tournamentId}/seasons`, {
        state: {
          selectedTournament: tournament,
          allTournaments: (location.state as any)?.allTournaments
        }
      })
    },
    { label: season?.seasonLabel || 'Season', active: true }
  ];

  if (loading) {
    return (
      <Container fluid>
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={breadcrumbItems} />
        </div>
        <ShimmerLoader type="table" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid>
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={breadcrumbItems} />
        </div>
        <div className="text-center py-5">
          <h4>Error Loading Season</h4>
          <p>{error}</p>
          <Button variant="primary" onClick={loadSeasonDetails}>
            Try Again
          </Button>
        </div>
      </Container>
    );
  }

  if (!tournament || !season) {
    return (
      <Container fluid>
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={breadcrumbItems} />
        </div>
        <div className="text-center py-5">
          <h4>Season Not Found</h4>
          <p>The requested season could not be found.</p>
          <Button variant="primary" onClick={() => navigate(`/manage-tournaments-fixtures/${tournamentId}/seasons`, {
            state: {
              selectedTournament: tournament,
              allTournaments: (location.state as any)?.allTournaments
            }
          })}>
            Back to Seasons
          </Button>
        </div>
      </Container>
    );
  }

  // Create a tournament-like object for the header (combining tournament and season info)
  const displayTournament: ManageTournament = {
    ...tournament,
    name: `${tournament.name} - ${season.seasonLabel} (${season.seasonYearRange})`
  };

  return (
    <div className="page-content">
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={breadcrumbItems} />
        </div>

        {/* Tournament Header */}
        <div style={{ padding: '0 15px' }}>
          <TournamentHeader
            tournament={displayTournament}
            onGoBack={() => navigate(`/manage-tournaments-fixtures/${tournamentId}/seasons`, {
              state: {
                selectedTournament: tournament,
                allTournaments: (location.state as any)?.allTournaments
              }
            })}
          />
        </div>

        {/* Tabs Navigation */}
        <div style={{ padding: '0 15px' }}>
          <Nav
            variant="tabs"
            className="tournament-tabs mb-4"
            style={{ flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', whiteSpace: 'nowrap', scrollbarWidth: 'none' }}
          >
            <Nav.Item><Nav.Link className="tournament-tab" active={activeTab === 'details'} onClick={() => setActiveTab('details')}><i className="ri-information-line me-2" />Details</Nav.Link></Nav.Item>
            <Nav.Item><Nav.Link className="tournament-tab" active={activeTab === 'age-categories'} onClick={() => setActiveTab('age-categories')}><i className="ri-group-line me-2" />Age Category</Nav.Link></Nav.Item>
            <Nav.Item><Nav.Link className="tournament-tab" active={activeTab === 'teams'} onClick={() => setActiveTab('teams')}><i className="ri-team-line me-2" />Teams</Nav.Link></Nav.Item>
            <Nav.Item><Nav.Link className="tournament-tab" active={activeTab === 'players'} onClick={() => setActiveTab('players')}><i className="ri-user-line me-2" />Players</Nav.Link></Nav.Item>
            {supports('standings') && <Nav.Item><Nav.Link className="tournament-tab" active={activeTab === 'standings'} onClick={() => setActiveTab('standings')}><i className="ri-bar-chart-line me-2" />Standings</Nav.Link></Nav.Item>}
            {supports('fixtures') && <Nav.Item><Nav.Link className="tournament-tab" active={activeTab === 'match-management'} onClick={() => setActiveTab('match-management')}><i className="ri-calendar-event-line me-2" />Matches</Nav.Link></Nav.Item>}
            {!isBadminton() && supports('playerStats') && <Nav.Item><Nav.Link className="tournament-tab" active={activeTab === 'player-stats'} onClick={() => setActiveTab('player-stats')}><i className="ri-line-chart-line me-2" />Player stats</Nav.Link></Nav.Item>}
            <Nav.Item><Nav.Link className="tournament-tab" active={activeTab === 'videos'} onClick={() => setActiveTab('videos')}><i className="ri-video-line me-2" />Videos</Nav.Link></Nav.Item>
            {/* Temporarily hide the season Gallery tab. */}
            {/* <Nav.Item><Nav.Link className="tournament-tab" active={activeTab === 'gallery'} onClick={() => setActiveTab('gallery')}><i className="ri-image-line me-2" />Gallery</Nav.Link></Nav.Item> */}
            <Nav.Item>
              <Nav.Link
                className="tournament-tab"
                active={activeTab === 'settings'}
                onClick={() => setActiveTab('settings')}
              >
                <i className="ri-settings-3-line me-2"></i>
                Settings
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                className="tournament-tab"
                active={activeTab === 'voting'}
                onClick={() => setActiveTab('voting')}
              >
                <i className="ri-thumb-up-line me-2"></i>
                Voting
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                className="tournament-tab"
                active={activeTab === 'predictions'}
                onClick={() => setActiveTab('predictions')}
              >
                <i className="ri-question-answer-line me-2"></i>
                Predictions
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </div>

        {/* Tab Content */}
        <div style={{ padding: '0 15px' }}>
          {activeTab === 'details' ? (
            <Card>
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Details</h5>
                  <div>
                    {isEditingSeason ? (
                      <>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="me-2"
                          onClick={handleCancelSeasonEdit}
                          disabled={seasonFormLoading}
                          style={{ borderRadius: '6px' }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={handleSeasonFormSubmit}
                          disabled={seasonFormLoading || isSeasonMediaUploading}
                          style={{ borderRadius: '6px' }}
                        >
                          {seasonFormLoading ? 'Saving...' : 'Save'}
                        </Button>
                      </>
                    ) : canManageTournament ? (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-2"
                        onClick={handleEditSeasonClick}
                        style={{ borderRadius: '6px' }}
                      >
                        <i className="ri-pencil-line me-1"></i>
                        Edit
                      </Button>
                    ) : null}
                    {canManageTournament && <Button
                      variant="outline-danger"
                      size="sm"
                      className="ms-3"
                      onClick={handleDeleteSeason}
                      disabled={!canDeleteSeason(season) || deleteLoading}
                      title={
                        !canDeleteSeason(season)
                          ? (!canDeleteTournament(tournament)
                            ? "Cannot delete season when tournament is ongoing or completed"
                            : "Cannot delete ongoing or completed seasons")
                          : "Delete Season"
                      }
                      style={{
                        borderRadius: '6px',
                        opacity: canDeleteSeason(season) ? 1 : 0.5,
                        cursor: canDeleteSeason(season) ? 'pointer' : 'not-allowed'
                      }}
                    >
                      <i className="ri-delete-bin-line me-1"></i>
                      {deleteLoading ? 'Deleting...' : 'Delete'}
                    </Button>}
                  </div>
                </div>
              </Card.Header>
              <Card.Body>
                {/* Success Message */}
                {successMessage && (
                  <div className="alert alert-success alert-dismissible fade show" role="alert">
                    <i className="ri-check-line me-2"></i>
                    {successMessage}
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setSuccessMessage(null)}
                    ></button>
                  </div>
                )}

                {/* Delete/Update Error Message */}
                {deleteError && (
                  <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    <i className="ri-error-warning-line me-2"></i>
                    {deleteError}
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setDeleteError(null)}
                    ></button>
                  </div>
                )}

                {isEditingSeason ? (
                  /* Edit Form */
                  <Form onSubmit={handleSeasonFormSubmit}>
                    <Row>
                      <Col md={12}>
                        <div className="mb-4">
                          <label className="form-label" style={{
                            fontSize: '14px',
                            color: '#6c757d',
                            fontWeight: '500',
                            marginBottom: '8px'
                          }}>
                            Season Name <span className="text-danger">*</span>
                          </label>
                          <Form.Control
                            type="text"
                            value={seasonFormData.name}
                            onChange={(e) => {
                              setSeasonFormData({ ...seasonFormData, name: e.target.value });
                              if (deleteError && /already exists/i.test(deleteError)) {
                                setDeleteError(null);
                              }
                              if (seasonFormErrors.name) {
                                setSeasonFormErrors({ ...seasonFormErrors, name: '' });
                              }
                            }}
                            isInvalid={!!seasonFormErrors.name}
                            placeholder="Enter season name"
                            style={{
                              fontSize: '16px',
                              fontWeight: '500',
                              color: '#212529',
                              borderColor: seasonFormErrors.name ? '#dc3545' : undefined
                            }}
                          />
                          {seasonFormErrors.name && (
                            <Form.Control.Feedback type="invalid">
                              {seasonFormErrors.name}
                            </Form.Control.Feedback>
                          )}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-4">
                          <label className="form-label" style={{
                            fontSize: '14px',
                            color: '#6c757d',
                            fontWeight: '500',
                            marginBottom: '8px'
                          }}>
                            Start Date <span className="text-danger">*</span>
                          </label>
                          <Form.Control
                            type="date"
                            value={seasonFormData.startDate}
                            onChange={(e) => {
                              setSeasonFormData({ ...seasonFormData, startDate: e.target.value });
                              if (seasonFormErrors.startDate) {
                                setSeasonFormErrors({ ...seasonFormErrors, startDate: '' });
                              }
                              if (seasonFormData.endDate && !seasonFormData.seasonYearRange) {
                                const calculatedRange = calculateSeasonYearRange(e.target.value, seasonFormData.endDate);
                                setSeasonFormData(prev => ({ ...prev, seasonYearRange: calculatedRange }));
                              }
                            }}
                            isInvalid={!!seasonFormErrors.startDate}
                            style={{
                              fontSize: '16px',
                              fontWeight: '500',
                              color: '#212529',
                              borderColor: seasonFormErrors.startDate ? '#dc3545' : undefined
                            }}
                          />
                          {seasonFormErrors.startDate && (
                            <Form.Control.Feedback type="invalid">
                              {seasonFormErrors.startDate}
                            </Form.Control.Feedback>
                          )}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-4">
                          <label className="form-label" style={{
                            fontSize: '14px',
                            color: '#6c757d',
                            fontWeight: '500',
                            marginBottom: '8px'
                          }}>
                            End Date <span className="text-danger">*</span>
                          </label>
                          <Form.Control
                            type="date"
                            value={seasonFormData.endDate}
                            min={seasonFormData.startDate || undefined}
                            onChange={(e) => {
                              setSeasonFormData({ ...seasonFormData, endDate: e.target.value });
                              if (seasonFormErrors.endDate) {
                                setSeasonFormErrors({ ...seasonFormErrors, endDate: '' });
                              }
                              if (seasonFormData.startDate && new Date(e.target.value) < new Date(seasonFormData.startDate)) {
                                setSeasonFormErrors({ ...seasonFormErrors, endDate: 'End date must be after start date' });
                              } else if (seasonFormErrors.endDate) {
                                const newErrors = { ...seasonFormErrors };
                                delete newErrors.endDate;
                                setSeasonFormErrors(newErrors);
                              }
                              if (seasonFormData.startDate && !seasonFormData.seasonYearRange) {
                                const calculatedRange = calculateSeasonYearRange(seasonFormData.startDate, e.target.value);
                                setSeasonFormData(prev => ({ ...prev, seasonYearRange: calculatedRange }));
                              }
                            }}
                            isInvalid={!!seasonFormErrors.endDate}
                            style={{
                              fontSize: '16px',
                              fontWeight: '500',
                              color: '#212529',
                              borderColor: seasonFormErrors.endDate ? '#dc3545' : undefined
                            }}
                          />
                          {seasonFormErrors.endDate && (
                            <Form.Control.Feedback type="invalid">
                              {seasonFormErrors.endDate}
                            </Form.Control.Feedback>
                          )}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-4">
                          <label className="form-label" style={{
                            fontSize: '14px',
                            color: '#6c757d',
                            fontWeight: '500',
                            marginBottom: '8px'
                          }}>
                            Location <span className="text-danger">*</span>
                          </label>
                          <Form.Control
                            type="text"
                            value={seasonFormData.location}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (SEASON_LOCATION_INPUT_PATTERN.test(value)) {
                                setSeasonFormData({ ...seasonFormData, location: value });
                                if (seasonFormErrors.location) {
                                  setSeasonFormErrors({ ...seasonFormErrors, location: '' });
                                }
                              }
                            }}
                            isInvalid={!!seasonFormErrors.location}
                            placeholder="Enter location"
                            style={{
                              fontSize: '16px',
                              fontWeight: '500',
                              color: '#212529',
                              borderColor: seasonFormErrors.location ? '#dc3545' : undefined
                            }}
                          />
                          {seasonFormErrors.location && (
                            <Form.Control.Feedback type="invalid">
                              {seasonFormErrors.location}
                            </Form.Control.Feedback>
                          )}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-4">
                          <label className="form-label" style={{
                            fontSize: '14px',
                            color: '#6c757d',
                            fontWeight: '500',
                            marginBottom: '8px'
                          }}>
                            Season Year Range <span className="text-danger">*</span>
                          </label>
                          <Form.Control
                            type="text"
                            value={seasonFormData.seasonYearRange}
                            onChange={(e) => {
                              const value = e.target.value;
                              const yearRangePattern = /^[\d\-]*$/;
                              if (yearRangePattern.test(value) || value === '') {
                                setSeasonFormData({ ...seasonFormData, seasonYearRange: value });
                                if (seasonFormErrors.seasonYearRange) {
                                  setSeasonFormErrors({ ...seasonFormErrors, seasonYearRange: '' });
                                }
                              }
                            }}
                            onBlur={() => {
                              const trimmedRange = seasonFormData.seasonYearRange.trim();
                              if (trimmedRange) {
                                const yearRangePattern = /^(\d{4})(-\d{4})?$/;
                                if (!yearRangePattern.test(trimmedRange)) {
                                  setSeasonFormErrors({ ...seasonFormErrors, seasonYearRange: 'Season year range must be in format "2025" or "2025-2026"' });
                                } else if (trimmedRange.includes('-')) {
                                  const [startYear, endYear] = trimmedRange.split('-').map(Number);
                                  if (endYear < startYear) {
                                    setSeasonFormErrors({ ...seasonFormErrors, seasonYearRange: 'End year must be greater than or equal to start year' });
                                  }
                                }
                              }
                            }}
                            isInvalid={!!seasonFormErrors.seasonYearRange}
                            placeholder="e.g., 2025 or 2025-2026"
                            style={{
                              fontSize: '16px',
                              fontWeight: '500',
                              color: '#212529',
                              borderColor: seasonFormErrors.seasonYearRange ? '#dc3545' : undefined
                            }}
                          />
                          {seasonFormErrors.seasonYearRange && (
                            <Form.Control.Feedback type="invalid">
                              {seasonFormErrors.seasonYearRange}
                            </Form.Control.Feedback>
                          )}
                          <small className="text-muted">
                            Format: YYYY (e.g., 2025) or YYYY-YYYY (e.g., 2025-2026)
                          </small>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-4">
                          <label className="form-label" style={{
                            fontSize: '14px',
                            color: '#6c757d',
                            fontWeight: '500',
                            marginBottom: '8px'
                          }}>
                            Status <span className="text-danger">*</span>
                          </label>
                          <Select
                            classNamePrefix="react-select"
                            value={seasonFormData.status ? { value: seasonFormData.status, label: seasonFormData.status.charAt(0).toUpperCase() + seasonFormData.status.slice(1) } : null}
                            onChange={(selectedOption) => {
                              setSeasonFormData({ ...seasonFormData, status: selectedOption ? selectedOption.value : '' });
                              if (seasonFormErrors.status) {
                                setSeasonFormErrors({ ...seasonFormErrors, status: '' });
                              }
                            }}
                            options={[
                              { value: 'upcoming', label: 'Upcoming' },
                              { value: 'ongoing', label: 'Ongoing' },
                              { value: 'completed', label: 'Completed' },
                              { value: 'cancelled', label: 'Cancelled' }
                            ]}
                            placeholder="Select status"
                            maxMenuHeight={200}
                            menuPosition="fixed"
                            menuPortalTarget={document.body}
                            styles={{
                              control: (base, state) => ({
                                ...base,
                                minHeight: '38px',
                                border: seasonFormErrors.status ? '1px solid #dc3545' : '1px solid #e0e0e0',
                                borderRadius: '6px',
                                fontSize: '16px',
                                fontWeight: '500',
                                color: '#212529',
                                boxShadow: state.isFocused ? (seasonFormErrors.status ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : '0 0 0 0.2rem rgba(0, 123, 255, 0.25)') : 'none'
                              }),
                              menu: (base) => ({
                                ...base,
                                zIndex: 99999,
                                maxHeight: 200,
                                overflow: 'hidden'
                              }),
                              menuPortal: (base) => ({
                                ...base,
                                zIndex: 99999
                              }),
                              menuList: (base) => ({
                                ...base,
                                maxHeight: 200,
                                padding: 0,
                                overflowY: 'auto',
                                overflowX: 'hidden'
                              }),
                              option: (base, state) => ({
                                ...base,
                                padding: '8px 12px',
                                fontSize: '14px',
                                backgroundColor: state.isFocused ? '#e3f2fd' : 'white',
                                color: state.isFocused ? '#1976d2' : '#212529'
                              }),
                              singleValue: (base) => ({
                                ...base,
                                fontSize: '16px',
                                fontWeight: '500',
                                color: '#212529'
                              })
                            }}
                          />
                          {seasonFormErrors.status && (
                            <div className="invalid-feedback d-block">
                              {seasonFormErrors.status}
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>
                    <Row className="mb-4">
                      <Col md={12}>
                        <h6 className="mb-3">Media</h6>
                      </Col>
                      <Col md={4}>
                        <SeasonHeroImageUpload
                          label="Season Vertical"
                          helperText={SEASON_VERTICAL_MEDIA_ENABLED ? 'Portrait artwork for Home and All Leagues. Recommended 2:3.' : 'Available after the portrait cutover.'}
                          value={seasonFormData.heroImage}
                          context={getSeasonMediaContext('portrait')}
                          previewAspectRatio="2 / 3"
                          disabled={seasonFormLoading || !SEASON_VERTICAL_MEDIA_ENABLED}
                          onUploadingChange={(uploading) => handleSeasonMediaUploadingChange('heroImage', uploading)}
                          onChange={(value) => handleSeasonMediaChange('heroImage', value)}
                        />
                      </Col>
                      <Col md={4}>
                        <SeasonHeroImageUpload
                          label="Season Horizontal - Web"
                          helperText="Landscape hero for public web. Falls back to App artwork when Web is empty, then Tournament Hero."
                          value={seasonFormData.detailHeroImage}
                          fallbackValue={seasonFormData.detailHeroImageApp}
                          fallbackLabel="Previewing App fallback"
                          context={getSeasonMediaContext('horizontal')}
                          previewAspectRatio="16 / 9"
                          disabled={seasonFormLoading}
                          onUploadingChange={(uploading) => handleSeasonMediaUploadingChange('detailHeroImage', uploading)}
                          onChange={(value) => handleSeasonMediaChange('detailHeroImage', value)}
                        />
                      </Col>
                      <Col md={4}>
                        <SeasonHeroImageUpload
                          label="Season Horizontal - App"
                          helperText="Landscape hero for native app. Falls back to Web artwork when App is empty, then Tournament Hero."
                          value={seasonFormData.detailHeroImageApp}
                          fallbackValue={seasonFormData.detailHeroImage}
                          fallbackLabel="Previewing Web fallback"
                          context={getSeasonMediaContext('horizontal-app')}
                          previewAspectRatio="16 / 9"
                          disabled={seasonFormLoading}
                          onUploadingChange={(uploading) => handleSeasonMediaUploadingChange('detailHeroImageApp', uploading)}
                          onChange={(value) => handleSeasonMediaChange('detailHeroImageApp', value)}
                        />
                      </Col>
                      <Col md={4}>
                        <SeasonHeroImageUpload
                          label="Default Match Background"
                          helperText="Shown behind team logos for matches under this season. Falls back to the overall default when empty."
                          value={seasonFormData.matchCardBackgroundImage}
                          context={getSeasonMediaContext('match-card-background')}
                          previewAspectRatio="16 / 9"
                          disabled={seasonFormLoading}
                          onUploadingChange={(uploading) => handleSeasonMediaUploadingChange('matchCardBackgroundImage', uploading)}
                          onChange={(value) => handleSeasonMediaChange('matchCardBackgroundImage', value)}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={12}>
                        <Row className="gy-4">
                          <Col md={12}><h6 className="mb-3">TV & Display Settings</h6></Col>
                          <Col md={12}><Row><Col md={4}><Form.Check type="switch" id="is-featured-on-tv" label="Featured on TV" checked={seasonFormData.isFeaturedOnTv} onChange={(event) => setSeasonFormData({ ...seasonFormData, isFeaturedOnTv: event.target.checked })} /></Col><Col md={4}><Form.Check type="switch" id="show-on-home-page" label="Show on Home Page" checked={seasonFormData.showOnHomePage} onChange={(event) => setSeasonFormData({ ...seasonFormData, showOnHomePage: event.target.checked })} /></Col></Row></Col>
                          <Col md={12}><Row><Col md={4}><Form.Label>TV Access Type</Form.Label><Form.Select value={seasonFormData.tvAccessType} onChange={(event) => setSeasonFormData({ ...seasonFormData, tvAccessType: event.target.value })}><option value="free">Free</option><option value="paid">Paid</option></Form.Select></Col><Col md={4}><Form.Label>Recording Access Type</Form.Label><Form.Select value={seasonFormData.recordingAccessType} onChange={(event) => setSeasonFormData({ ...seasonFormData, recordingAccessType: event.target.value })}><option value="free">Free</option><option value="paid">Paid</option></Form.Select></Col><Col md={4}><Form.Label>Highlight Access Type</Form.Label><Form.Select value={seasonFormData.highlightAccessType} onChange={(event) => setSeasonFormData({ ...seasonFormData, highlightAccessType: event.target.value })}><option value="free">Free</option><option value="paid">Paid</option></Form.Select></Col></Row></Col>
                          {seasonFormData.tvAccessType === 'paid' && (
                          <Col md={12}><Row>{seasonFormData.tvAccessType === 'paid' && <Col md={4}><Form.Check type="switch" id="match-tv-pass" label="Enable Match TV Pass" checked={seasonFormData.matchPassEnabled} onChange={(event) => setSeasonFormData({ ...seasonFormData, matchPassEnabled: event.target.checked })} />{seasonFormData.matchPassEnabled && <><Form.Label className="mt-2">Match override price (INR)</Form.Label><Form.Control type="number" min="0" step="0.01" value={seasonFormData.matchPassPrice} onChange={(event) => setSeasonFormData({ ...seasonFormData, matchPassPrice: event.target.value })} placeholder="Optional" /></>}</Col>}<Col md={4}><Form.Check type="switch" id="season-tv-pass" label="Enable Season TV Pass" checked={seasonFormData.seasonPassEnabled} onChange={(event) => setSeasonFormData({ ...seasonFormData, seasonPassEnabled: event.target.checked })} />{seasonFormData.seasonPassEnabled && <><Form.Label className="mt-2">Season override price (INR)</Form.Label><Form.Control type="number" min="0" step="0.01" value={seasonFormData.seasonPassPrice} onChange={(event) => setSeasonFormData({ ...seasonFormData, seasonPassPrice: event.target.value })} placeholder="Optional" /></>}</Col><Col md={4}><Form.Check type="switch" id="tournament-tv-pass" label="Enable Tournament TV Pass" checked={seasonFormData.tournamentPassEnabled} onChange={(event) => setSeasonFormData({ ...seasonFormData, tournamentPassEnabled: event.target.checked })} /></Col></Row></Col>
                          )}
                          <Col md={12}><Row><Col md={4}><Form.Label>Sort Order</Form.Label><Form.Control type="number" min="1" value={seasonFormData.sortOrder} onChange={(event) => setSeasonFormData({ ...seasonFormData, sortOrder: event.target.value })} /></Col><Col md={4}><Form.Label>TV Preview Duration (secs)</Form.Label><Form.Control type="number" min="0" value={seasonFormData.tvPreviewDuration} onChange={(event) => setSeasonFormData({ ...seasonFormData, tvPreviewDuration: event.target.value })} /></Col></Row></Col>
                        </Row>
                      </Col>
                    </Row>
                  </Form>
                ) : (
                  /* Read-only View */
                  <Row>
                    <Col md={6}>
                      <div className="mb-4">
                        <label style={{ fontSize: '14px', color: '#6c757d', fontWeight: '500', marginBottom: '8px' }}>
                          Season Name
                        </label>
                        <p style={{ fontSize: '16px', fontWeight: '500', color: '#212529', margin: '0' }}>
                          {season.seasonLabel}
                        </p>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-4">
                        <label style={{ fontSize: '14px', color: '#6c757d', fontWeight: '500', marginBottom: '8px' }}>
                          Season Year Range
                        </label>
                        <p style={{ fontSize: '16px', fontWeight: '500', color: '#212529', margin: '0' }}>
                          {season.seasonYearRange || '-'}
                        </p>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-4">
                        <label style={{ fontSize: '14px', color: '#6c757d', fontWeight: '500', marginBottom: '8px' }}>
                          Start Date
                        </label>
                        <p style={{ fontSize: '16px', fontWeight: '500', color: '#212529', margin: '0' }}>
                          {season.startDate ? formatDate(season.startDate) : '-'}
                        </p>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-4">
                        <label style={{ fontSize: '14px', color: '#6c757d', fontWeight: '500', marginBottom: '8px' }}>
                          End Date
                        </label>
                        <p style={{ fontSize: '16px', fontWeight: '500', color: '#212529', margin: '0' }}>
                          {season.endDate ? formatDate(season.endDate) : '-'}
                        </p>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-4">
                        <label style={{ fontSize: '14px', color: '#6c757d', fontWeight: '500', marginBottom: '8px' }}>
                          Location
                        </label>
                        <p style={{ fontSize: '16px', fontWeight: '500', color: '#212529', margin: '0' }}>
                          {season.location || '-'}
                        </p>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-4">
                        <label style={{ fontSize: '14px', color: '#6c757d', fontWeight: '500', marginBottom: '8px' }}>
                          Status
                        </label>
                        <div>
                          <Badge
                            bg={season.status === 'completed' ? 'success' :
                              season.status === 'ongoing' ? 'primary' :
                                season.status === 'upcoming' ? 'warning' : 'danger'}
                            style={{ fontSize: '14px', padding: '6px 12px', borderRadius: '6px' }}
                          >
                            {season.status?.charAt(0).toUpperCase() + season.status?.slice(1)}
                          </Badge>
                        </div>
                      </div>
                    </Col>
                  </Row>
                )}
              </Card.Body>
            </Card>
          ) : activeTab === 'age-categories' ? (
            <div style={{ padding: '0 15px' }}>
              <AgeCategoryManagement
                tournamentId={tournamentId || ''}
                tournamentName={tournament?.name || ''}
                tournamentSports={tournament?.sports || ''}
                tournamentSeasonId={seasonId} // Pass tournamentSeason id for season context
                search={ageCategorySearch}
                onSearch={setAgeCategorySearch}
                ageCategories={ageCategories}
                ageCategoriesLoading={ageCategoriesLoading}
                page={ageCategoryPage}
                pageSize={ageCategoryPageSize}
                totalPages={ageCategoryTotalPages}
                totalCount={ageCategoryTotalCount}
                onPageChange={setAgeCategoryPage}
                onPageSizeChange={size => { setAgeCategoryPageSize(size); setAgeCategoryPage(1); }}
                onRefresh={refreshAgeCategories}
                typeFilter={typeFilter}
                setTypeFilter={setTypeFilter}
                formatFilter={formatFilter}
                setFormatFilter={setFormatFilter}
                typeOptions={typeOptions}
                formatOptions={formatOptions}
                isStandingsContext={false}
                canConfigureTies={supports('ties') && isSeasonFeatureEnabled('TOURNAMENT_TIES')}
                canConfigureAwards={isSeasonFeatureEnabled('TOURNAMENT_AWARDS')}
                canConfigureVoting={isSeasonFeatureEnabled('TOURNAMENT_VOTING')}
              />
            </div>
          ) : activeTab === 'teams' ? (
            <div style={{ padding: '0 15px' }}>
              <TeamsTable
                teams={teams}
                search={teamSearch}
                onSearch={setTeamSearch}
                ageCategoryFilter={teamsAgeCategoryFilter}
                onAgeCategoryFilterChange={setTeamsAgeCategoryFilter}
                onEdit={handleEditTeam}
                onDelete={handleDeleteTeam}
                onAdd={handleAddTeam}
                ageCategories={ageCategories}
                tournamentId={tournamentId || ''}
                tournamentSeasonId={seasonId} // Pass tournamentSeason id for season context
                seasonNumber={season?.seasonNumber} // Pass season number for conditional "Add Existing Team" button
                teamsLoading={teamsLoading}
                page={teamsPage}
                pageSize={teamsPageSize}
                totalPages={teamsTotalPages}
                totalCount={teamsTotalCount}
                onPageChange={setTeamsPage}
                onPageSizeChange={size => { setTeamsPageSize(size); setTeamsPage(1); }}
                tournamentSports={tournament?.sports || getSport()}
              />
            </div>
          ) : activeTab === 'players' ? (
            <div style={{ padding: '0 15px' }}>
              <PlayersTable
                players={players}
                search={playerSearch}
                onSearch={setPlayerSearch}
                ageCategoryFilter={playersAgeCategoryFilter}
                onAgeCategoryFilterChange={setPlayersAgeCategoryFilter}
                onAdd={handleAddPlayer}
                onEdit={handleEditPlayer}
                onDelete={handleDeletePlayer}
                teams={teamOptions}
                ageCategories={ageCategoryOptionsForPlayers}
                loading={playersLoading}
                page={playersPage}
                pageSize={playersPageSize}
                totalPages={playersTotalPages}
                totalCount={playersTotalCount}
                onPageChange={setPlayersPage}
                onPageSizeChange={size => { setPlayersPageSize(size); setPlayersPage(1); }}
                tournamentId={tournamentId || ''}
                tournamentSeasonId={seasonId} // Pass tournamentSeason id for season context
                tournamentSports={tournament?.sports || getSport()}
                onRefresh={() => setPlayersRefresh(v => !v)}
              />
            </div>
          ) : activeTab === 'match-management' ? (
            <div style={{ padding: '0 15px' }}>
              <FixturesTable
                fixtures={fixtures}
                search={fixtureSearch}
                onSearch={setFixtureSearch}
                ageCategoryFilter={fixturesAgeCategoryFilter}
                onAgeCategoryFilterChange={setFixturesAgeCategoryFilter}
                statusFilter={matchStatusFilter}
                onStatusFilterChange={setMatchStatusFilter}
                onAdd={handleAddFixture}
                onEdit={handleEditFixture}
                onDelete={handleDeleteFixture}
                ageCategories={ageCategoryOptionsForPlayers}
                fixturesLoading={fixturesLoading}
                page={fixturesPage}
                pageSize={fixturesPageSize}
                totalPages={fixturesTotalPages}
                totalCount={fixturesTotalCount}
                onPageChange={setFixturesPage}
                onPageSizeChange={size => { setFixturesPageSize(size); setFixturesPage(1); }}
                tournamentId={tournamentId || ''}
                tournamentSports={tournament?.sports || getSport()}
                tournamentSeasonId={seasonId} // Pass tournamentSeason id for season context
                initialOpenFixture={null}
                navigateToDetailPage={true}
                onDownloadExcel={handleDownloadExcel}
                isDownloadingExcel={isDownloadingExcel}
              />
            </div>
          ) : activeTab === 'player-stats' && !isBadminton() ? (
            <div style={{ padding: '0 15px' }}>
              <PlayersTable
                players={playerStats}
                search={playerStatsSearch}
                onSearch={setPlayerStatsSearch}
                ageCategoryFilter={playerStatsAgeCategoryFilter}
                onAgeCategoryFilterChange={setPlayerStatsAgeCategoryFilter}
                onAdd={handleAddPlayer}
                onEdit={handleEditPlayer}
                onDelete={handleDeletePlayer}
                teams={teamOptions}
                ageCategories={ageCategoryOptionsForPlayers}
                loading={playerStatsLoading}
                page={playerStatsPage}
                pageSize={playerStatsPageSize}
                totalPages={playerStatsTotalPages}
                totalCount={playerStatsTotalCount}
                onPageChange={setPlayerStatsPage}
                onPageSizeChange={size => { setPlayerStatsPageSize(size); setPlayerStatsPage(1); }}
                tournamentId={tournamentId || ''}
                tournamentSeasonId={seasonId} // Pass tournamentSeason id for season context
                tournamentSports={tournament?.sports || getSport()}
                isPlayerStats={true}
                onOpenPlayerStatsDetail={(playerId: string) => navigate(`/manage-tournaments-fixtures/${tournamentId}/player-stats/${playerId}`, {
                  state: {
                    tournamentSeasonId: seasonId,
                    fromTournamentDetailPage: false,
                    selectedTournament: tournament // Include tournament so sport can be extracted
                  }
                })}
                onRefresh={() => setPlayerStatsRefresh(v => !v)}
              />
            </div>
          ) : activeTab === 'standings' ? (
            <div style={{ padding: '0 15px' }}>
              {standingsError ? (
                <div className="alert alert-danger">{standingsError}</div>
              ) : (
                <AgeCategoryManagement
                  ageCategories={standings}
                  search={standingsSearch}
                  onSearch={setStandingsSearch}
                  onRefresh={() => setStandingsRefresh(v => !v)}
                  tournamentId={tournamentId || ''}
                  tournamentName={tournament?.name || ''}
                  tournamentSports={tournament?.sports || ''}
                  tournamentSeasonId={seasonId} // Pass tournamentSeason id for season context
                  ageCategoriesLoading={standingsLoading}
                  page={standingsPage}
                  pageSize={standingsPageSize}
                  totalPages={standingsTotalPages}
                  totalCount={standingsTotalCount}
                  onPageChange={setStandingsPage}
                  onPageSizeChange={size => { setStandingsPageSize(size); setStandingsPage(1); }}
                  typeFilter={typeFilter}
                  setTypeFilter={setTypeFilter}
                  formatFilter={formatFilter}
                  setFormatFilter={setFormatFilter}
                  typeOptions={typeOptions}
                  formatOptions={formatOptions}
                  isStandingsContext={true}
                />
              )}
            </div>
          ) : activeTab === 'settings' ? (
            <Card>
              <Card.Header><h5 className="mb-0">Season features</h5></Card.Header>
              <Card.Body>
                {featureToggleError && <div className="alert alert-warning">{featureToggleError}</div>}
                {featureToggles.length ? featureToggles
                  // Temporarily hide the season Gallery toggle.
                  .filter((toggle) => toggle.name !== 'TOURNAMENT_GALLERY')
                  .map((toggle) => (
                  <Form.Check
                    className="mb-3"
                    key={toggle.name}
                    type="switch"
                    id={`feature-${toggle.name}`}
                    label={toggle.label}
                    checked={toggle.enabled}
                    onChange={(event) => updateFeatureToggle(toggle, event.target.checked)}
                    disabled={!canManageTournament || savingFeatureToggles.includes(toggle.name)}
                  />
                )) : featureTogglesLoading ? (
                  <span className="text-muted">Loading season settings…</span>
                ) : (
                  <span className="text-muted">No season features are available.</span>
                )}
              </Card.Body>
            </Card>
          ) : activeTab === 'videos' && tournamentId && seasonId ? (
            <VideosTab
              tournamentId={tournamentId}
              seasonId={seasonId}
              returnPath={`/manage-tournaments-fixtures/${tournamentId}/seasons/${seasonId}`}
              parentState={{
                selectedTournament: tournament,
                allTournaments: (location.state as any)?.allTournaments,
              }}
            />
          ) : /* Temporarily hide the season Gallery tab content.
          activeTab === 'gallery' && tournamentId && seasonId ? (
            <GalleryTab
              tournamentId={tournamentId}
              seasonId={seasonId}
              seasonName={season?.seasonLabel || tournament?.name || 'Season'}
              sport={tournament?.sports}
              returnPath={`/manage-tournaments-fixtures/${tournamentId}/seasons/${seasonId}`}
              parentState={{
                selectedTournament: tournament,
                allTournaments: (location.state as any)?.allTournaments,
              }}
            />
          ) : */ activeTab === 'voting' ? (
            <div style={{ padding: '0 15px' }}>
              <VotingManagement
                tournamentId={tournamentId}
                seasonId={seasonId}
                tournamentSports={tournament?.sports}
                ageCategoryOptionsList={ageCategoryOptionsForPlayers}
              />
            </div>
          ) : activeTab === 'predictions' ? (
            <div style={{ padding: '0 15px' }}>
              <PredictionsManagement
                tournamentId={tournamentId}
                seasonId={seasonId}
                tournamentSports={tournament?.sports}
              />
            </div>
          ) : null}
        </div>
      </Container>

      {/* Download Excel Modal */}
      <Modal
        show={showDownloadExcelModal}
        onHide={handleCloseDownloadExcelModal}
        size="lg"
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Download Excel</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {excelDownloadError && (
            <div className="alert alert-danger mb-3" role="alert">
              {excelDownloadError}
            </div>
          )}
          <Form>
            <Row className="g-3">
              <Col md={8}>
                <Form.Label className="mb-2 d-block">
                  Select Date Range <span className="text-danger">*</span>
                  <small className="text-muted d-block mt-1" style={{ fontSize: '0.85rem' }}>
                    Select a single date or a date range. Dates with matches are marked with a green dot.
                  </small>
                </Form.Label>
                {loadingCalendarEvents ? (
                  <div className="text-center py-4">
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Loading calendar events...
                  </div>
                ) : (
                  <MatchCalendar
                    calendarEvents={calendarEvents}
                    startDate={excelDownloadStartDate}
                    endDate={excelDownloadEndDate}
                    onStartDateChange={(date) => {
                      setExcelDownloadStartDate(date);
                      setExcelDownloadError(null);
                    }}
                    onEndDateChange={(date) => {
                      setExcelDownloadEndDate(date);
                      setExcelDownloadError(null);
                    }}
                    disabled={isDownloadingExcel}
                  />
                )}
                {!excelDownloadStartDate && (
                  <div className="text-danger small mt-2">
                    Please select at least a start date
                  </div>
                )}
              </Col>
              <Col md={4}>
                <Form.Label>Status <span className="text-danger">*</span></Form.Label>
                <Select
                  classNamePrefix="react-select"
                  value={excelDownloadStatus ? { value: excelDownloadStatus, label: excelDownloadStatus === 'pending' ? 'Upcoming' : 'Completed' } : null}
                  onChange={(option: any) => {
                    setExcelDownloadStatus(option?.value || '');
                    setExcelDownloadError(null);
                  }}
                  options={[
                    { value: 'pending', label: 'Upcoming' },
                    { value: 'completed', label: 'Completed' }
                  ]}
                  placeholder="Select Status"
                  isClearable={false}
                  isDisabled={isDownloadingExcel}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '38px',
                      border: excelDownloadError && !excelDownloadStatus ? '1px solid #dc3545' : '1px solid #ced4da',
                      borderRadius: '6px'
                    })
                  }}
                />
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDownloadExcelModal} disabled={isDownloadingExcel}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleExcelDownloadSubmit}
            disabled={isDownloadingExcel || !excelDownloadStartDate || !excelDownloadStatus}
          >
            {isDownloadingExcel ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Downloading...
              </>
            ) : (
              <>
                <i className="ri-file-excel-line me-2"></i>
                Download Excel
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SeasonDetailPage;
